import { createActionGroup, emptyProps, props } from "@ngrx/store";

import { Delta } from 'quill/core';

import { Asset, BaseCalendarEventFragment, CalendarEventForDispatchFragment, CalendarEventForScheduleFragment, EditProfileMutationVariables, OptimizeDateOutput, Role, User } from "../../../../generated/graphql.generated";

import { BookOffInput } from '../components/dispatch-crew-settings/dispatch-crew-settings.component';

import { CrewRoleOptions, CrewScheduleSendStatus, CrewSortOptions, DispatchEventStatusOptions, DispatchGroupOptions, DispatchPendingChange, DispatchUserStatusOptions, PotentialCrewMember, UpdateCrewActionPayload } from "./dispatch.reducer";

export interface CrewRequirement {
	crewLeads: number;
	crewMembers: number;
	opsPeople: number;
}

/** One parsed entry of the calendarEvents.defaultBlocks zone config. */
export interface DefaultBlockEntry {
	blockType: 'ops-manager' | 'reserve' | 'non-billable';
	name: string;
	startHour: number;
	endHour: number;
	crewRequirement?: CrewRequirement;
}

export interface NonBillableEventInput {
	userId?: string;
	start: number;
	end: number;
	description: string;
	reason: NonBillableReason;
	selectedDate: Date;
	eventId: string;
	crewRequirement?: CrewRequirement;
}

export type NonBillableReason = 'admin' | 'training' | 'meeting' | 'maintenance' | 'other';

export interface ReserveEventInput {
	userId?: string;
	start: number;
	end: number;
	selectedDate: Date;
	description?: string;
	eventId: string;
	crewRequirement?: CrewRequirement;
}

export interface OpsManagerEventInput {
	userId?: string;
	start: number;
	end: number;
	selectedDate: Date;
	description?: string;
	eventId: string;
	crewRequirement?: CrewRequirement;
}

/**
 * An OPS task — a dispatch-page task the office adds for ops/crew
 * (pickups/deliveries, warehouse runs, permits, first-day orientation…).
 * Ray's "OPS tasks" (item B.6): rendered as their own route rows with a
 * full card, and included in the crew email for routes it's assigned to.
 */
export interface OpsTaskEventInput {
	eventId: string;
	selectedDate: Date;
	title: string;
	start: number;
	end: number;
	/** Crew notes — serialized Quill delta (same shape as job.crewSummary) */
	notes?: string;
	clientName?: string;
	clientPhone?: string;
	clientEmail?: string;
	/** Optional job association (omnisearch by job code) */
	jobId?: string;
	jobCode?: string;
	/** User IDs assigned to execute the task */
	attendeeIds?: string[];
}

export const DispatchActions = createActionGroup({
	source: "Dispatch",
	events: {
		"Component Hard Refreshed": emptyProps(),

		// Init
		"Init Crew": emptyProps(),

		// List events actions
		"Hard Refresh Events": props<{
			date: Date | null;
		}>(),
		"Events Loading": emptyProps(),
		"Events Loaded": props<{
			events: CalendarEventForDispatchFragment[];
		}>(),
		"Events Load Error": props<{
			error: Error;
		}>(),
		"Replay Pending Crew Notes": props<{
			changes: DispatchPendingChange[];
			events: CalendarEventForDispatchFragment[];
		}>(),

		// List events filters
		"Set Dispatch Date": props<{
			date: Date;
		}>(),
		// Map view range support: inclusive end of the visible window (null
		// = open-ended → the fetch effect uses the single day's bounds).
		"Set Dispatch Date To": props<{
			date: Date | null;
		}>(),
		"Set Event Status": props<{
			eventStatus: DispatchEventStatusOptions;
		}>(),
		// Local sort
		"Set Group By": props<{
			groupBy: DispatchGroupOptions;
		}>(),

		// List users
		"Set User Search": props<{
			search: string;
		}>(),
		"Set Filtered Crew Ids": props<{
			ids: string[] | null;
		}>(),
		"Set User Role": props<{
			role: CrewRoleOptions;
		}>(),
		"Set User Status": props<{
			status: DispatchUserStatusOptions;
		}>(),
		"Set Crew Sort": props<{
			sort: CrewSortOptions;
		}>(),
		"Users Loading": emptyProps(),
		"Users Loaded": props<{
			users: User[];
		}>(),
		"Users Load Error": props<{
			error: Error;
		}>(),


		// List Roles
		"Roles Loading": emptyProps(),
		"Roles Loaded": props<{
			roles: Role[];
		}>(),
		"Roles Loaded On Component Hard Refresh": props<{
			roles: Role[];
		}>(),
		"Roles Load Error": props<{
			error: Error;
		}>(),


		// List Assets
		"Assets Loading": emptyProps(),
		"Assets Loaded": props<{
			assets: Asset[];
		}>(),
		"Assets Load Error": props<{
			error: Error;
		}>(),

		// Assign Crew Member
		"Update Crew": props<{
			edits: UpdateCrewActionPayload[]
		}>(),
		"Update Crew Success": props<{
			edits: UpdateCrewActionPayload[]
		}>(),
		"Update Crew Error": props<{
			error: string,
			edits: UpdateCrewActionPayload[]
		}>(),

		// Performance requirement on event
		"Event Performance Requirement Changed": props<{
			eventId: string;
			performanceRequirement: number;
		}>(),
		"Event Performance Requirement Save Success": props<{
			eventId: string;
			performanceRequirement: number;
		}>(),
		"Event Performance Requirement Save Error": props<{
			eventId: string;
			error: string;
		}>(),

		// crew schedule (legacy - kept for backwards compat)
		"Send Crew Schedule Dialog Opened": emptyProps(),
		"Recepients for Crew Schedule Changed": props<{
			recepients: PotentialCrewMember[];
		}>(),
		"Crew Schedule Sent": props<{
			contents: Delta,
			subject: Delta;
		}>(),
		"Crew Schedule Send Success": emptyProps(),
		"Crew Schedule Send Error": emptyProps(),

		// Send Schedule to Crew dialog (new)
		"Send Schedule Dialog Opened": emptyProps(),

		/**
		 * Fired after dialog opens with the initial crew list.
		 * Pre-seeds sendScheduleSelectedCrew so Select All Changed/Unsent works.
		 */
		"Send Schedule Crew Initialized": props<{
			/** Map of assetId -> array of { userId, defaultSelected } */
			initialSelections: Array<{ assetId: string; userId: string; selected: boolean }>;
		}>(),
		"Send Schedule Dialog Closed": emptyProps(),

		"Send Schedule Crew Toggled": props<{
			userId: string;
			assetId: string;
			selected: boolean;
		}>(),

		/** UI action — triggers an effect that computes and applies selections */
		"Send Schedule All Changed Toggled": emptyProps(),
		/** Carries the computed selections to apply — dispatched by the effect */
		"Send Schedule Bulk Selection Applied": props<{
			selections: Array<{ assetId: string; userId: string; selected: boolean }>;
		}>(),
		"Send Schedule Deselect All": emptyProps(),

		"Send Schedule Custom Note Changed": props<{
			assetId: string;
			note: string;
		}>(),

		"Send Schedule Crew Custom Note Changed": props<{
			userId: string;
			note: string;
		}>(),

		"Send Schedule Preview Requested": props<{
			assetId: string;
			userId: string;
		}>(),

		"Send Schedule Preview Loaded": props<{
			assetId: string;
			userId: string;
			emailHtml: string;
			emailSubject: string;
			smsBody: string;
		}>(),

		"Send Schedule Preview Error": props<{
			assetId: string;
			userId: string;
			error: string;
		}>(),

		"Send Schedule Asset Threads Loading": props<{
			assetId: string;
		}>(),

		"Send Schedule Asset Threads Loaded": props<{
			assetId: string;
			threads: any[];
		}>(),

		"Send Schedule Asset Threads Load Error": props<{
			assetId: string;
			error: string;
		}>(),

		"Send Schedule Crew Statuses Loading": emptyProps(),

		"Send Schedule Crew Statuses Loaded": props<{
			statuses: Array<{ userId: string; assetId: string; status: CrewScheduleSendStatus; lastSentHash?: string }>;
		}>(),

		"Send Schedule Crew Statuses Load Error": props<{
			error: string;
		}>(),

		"Send Schedule Submitted": props<{
			assetId: string;
			/** Groups the per-route submits of one send() click so draft cleanup waits for the whole batch. */
			batchId: string;
			date: string;
			recipients: Array<{
				userId: string;
				scheduleHash: string;
				customNoteForCrew?: string;
			}>;
			customNoteForAsset?: string;
		}>(),

		"Send Schedule Batch Started": props<{
			batchId: string;
			routeCount: number;
		}>(),

		"Send Schedule Submit Success": props<{
			assetId: string;
			batchId: string;
			/** User IDs that were successfully sent to — used to update crew status badges. */
			sentUserIds: string[];
		}>(),

		"Send Schedule Submit Error": props<{
			assetId: string;
			batchId: string;
			error: string;
		}>(),

		// Dispatched by BatchDraftCleanup when every route of a batch sent
		// successfully — the only point at which sent users' typed drafts are
		// cleared (per-route clears would starve later routes of the note).
		"Send Schedule Crew Drafts Clear Sent": props<{
			batchId: string;
			sentUserIds: string[];
		}>(),

		// #7283 (Ray): crew personal notes typed in the send dialog stay
		// visible on the board across reloads — restored from localStorage.
		"Send Schedule Crew Notes Restored": props<{
			notes: Record<string, string>;
		}>(),


		"Crew Book Off Event Created Success": props<{
			calendarEvent: BaseCalendarEventFragment,
		}>(),

		"Crew Book Off Event Created Error": props<{
			error: Error,
		}>(),

		"Crew Book Off Event Deleted Success": props<{
			bookOffId: string;
		}>(),
		"Crew Book Off Deleted Error": props<{
			error: Error;
		}>(),


		// Crew Rate and Availability
		"Edit Crew Settings User Selected": props<{
			user: User;
		}>(),

		"Start Create Crew Member": props<{
			searchName?: string;
		}>(),
		"Create Crew Member": props<{
			user: Partial<User>;
		}>(),
		"Create Crew Member Success": props<{
			user: User;
		}>(),
		"Create Crew Member Error": props<{
			error: Error;
		}>(),

		"Update Draft Crew Member": props<{
			user: Partial<User>;
		}>(),
		"Set Creating User": props<{
			isCreating: boolean;
		}>(),

		// Crew Details
		"Crew Settings Updated": props<{
			id: string;
			capabilities?: string[];
			rateHourly?: number;
			overtimeRate?: number;
			maxHoursPerDay?: number;
			maxHoursPerWeek?: number;
			payPeriodStart?: number;
			availability?: string[];
			available?: boolean;
			timezone?: string;
			givenName?: string;
			familyName?: string;
			phone?: string;
			email?: string;
		}>(),

		"Edit Crew Settings Dialog Closed": emptyProps(),
		"Crew Settings Saved Successfully": props<EditProfileMutationVariables>(),
		"Error Saving Crew Settings": props<{
			error: Error;
		}>(),

		// Assign Crew Members with AI
		"Assign Crew Members With AI Clicked": emptyProps(),
		"Crew Members Assigned with AI successfully": props<{
			output: OptimizeDateOutput;
		}>(),
		"Failed to assign crew members with AI": props<{
			error: Error,
		}>(),
		/** The optimizer ran but no schedule satisfies every constraint. */
		"Crew Assignment Infeasible": props<{
			reasons: string[];
		}>(),
		"Assign Crew Result Dismissed": emptyProps(),

		"Remove All Locks": emptyProps(),
		"Unassign All Unlocked Crew": emptyProps(),

		// Download Crew List
		"Download Crew List As Csv": props<{ crew?: PotentialCrewMember[] }>(),

		// Upload Crew List
		"Upload Crew List Csv": props<{ csvData: string }>(),
		"Crew List Updated": emptyProps(),

		// Crew Book-Off
		"Crew Book Off Created": props<{
			newBookOff: BookOffInput;
		}>(),

		"Update Crew Book Off Events": props<{
			bookOffEvents: CalendarEventForScheduleFragment[];
		}>(),
		"Delete Crew Book Off Event": props<{ bookOffId: string }>(),

		"Crew Settings Tab Changed": props<{ index: number }>(),

		"Next Crew Member Clicked": emptyProps(),
		"Previous Crew Member Clicked": emptyProps(),

		// Worker History for Overtime Calculation
		"Load Worker History": props<{ userIds: string[], date: Date }>(),
		"Worker History Loaded": props<{ events: CalendarEventForDispatchFragment[] }>(),
		"Worker History Load Error": props<{ error: Error }>(),

		// Map View
		"Toggle Map Route Visibility": props<{ routeKey: string }>(),
		"Set Hidden Map Routes": props<{ hiddenRoutes: string[] }>(),
		"Load Dock Location": emptyProps(),
		"Set Dock Location": props<{ location: any }>(),
		"Update Asset Location": props<{ assetId: string, coordinates: { latitude: number, longitude: number } }>(),

		// Non-Billable Events (admin hours, training, meetings, etc.)
		"Non Billable Event Created": props<{
			input: NonBillableEventInput;
		}>(),
		"Non Billable Event Create Success": props<{
			calendarEvent: BaseCalendarEventFragment,
		}>(),
		"Non Billable Event Create Error": props<{
			error: Error,
		}>(),

		// Reserve Events (pre-assign crew to a pool for forward scheduling)
		"Reserve Event Created": props<{
			input: ReserveEventInput;
		}>(),
		"Reserve Event Create Success": props<{
			calendarEvent: BaseCalendarEventFragment,
		}>(),
		"Reserve Event Create Error": props<{
			error: Error,
		}>(),

		// Ops Manager Events (ops manager assigned to a day as an event, not a comment)
		"Ops Manager Event Created": props<{
			input: OpsManagerEventInput;
		}>(),
		"Ops Manager Event Create Success": props<{
			calendarEvent: BaseCalendarEventFragment,
		}>(),
		"Ops Manager Event Create Error": props<{
			error: Error,
		}>(),

		// OPS Tasks (dispatch-page tasks for ops/crew — Ray's OPS section)
		"Ops Task Created": props<{
			input: OpsTaskEventInput;
		}>(),
		"Ops Task Edited": props<{
			eventId: string;
			input: Partial<OpsTaskEventInput>;
			eventName: string;
		}>(),
		"Ops Task Edit Success": emptyProps(),
		"Ops Task Edit Error": props<{
			error: Error;
		}>(),

		// Default blocks (calendarEvents.defaultBlocks zone config)
	/** Fired on load — loads and parses the default blocks config for the zone. */
	"Default Blocks Config Loading": emptyProps(),
	"Default Blocks Config Loaded": props<{
		entries: DefaultBlockEntry[];
	}>(),
	"Default Blocks Config Load Error": props<{
		error: Error;
	}>(),
	/**
	 * Auto-create the missing default blocks for the loaded date. Dispatched
	 * by an effect after events + config are both available (and not while a
	 * save is in flight). Each missing entry becomes a createEvent pending
	 * change with the autoGenerated metadata flag. `date` is the local ISO
	 * date the ensure-run covers; it is recorded even when nothing was
	 * missing so repeated refreshes don't re-run matching.
	 */
	"Missing Default Blocks Ensured": props<{
		date: string;
		inputs: Array<{
			eventId: string;
			blockType: 'ops-manager' | 'reserve' | 'non-billable';
			name: string;
			start: number;
			end: number;
			crewRequirement?: CrewRequirement;
			/** autoGenerated metadata flag — marks the block as config-created. */
			metadata?: Record<string, string>;
		}>;
	}>(),
	/** Reset button — deletes auto-generated blocks and re-creates them from config. */
	"Default Blocks Reset Clicked": emptyProps(),
	/** Effect-computed reset payload — delete these ids, create these blocks. */
	"Default Blocks Reset Confirmed": props<{
		deleteEventIds: string[];
		inputs: Array<{
			eventId: string;
			blockType: 'ops-manager' | 'reserve' | 'non-billable';
			name: string;
			start: number;
			end: number;
			crewRequirement?: CrewRequirement;
			/** autoGenerated metadata flag — marks the block as config-created. */
			metadata?: Record<string, string>;
		}>;
	}>(),

	// Pending changes queue (save/discard model)
		"Load Pending Changes From Storage": props<{
			changes: DispatchPendingChange[],
		}>(),
		"Pending Change Added": props<{
			change: DispatchPendingChange,
		}>(),
		"Pending Change And Optimistic Update": props<{
			change: DispatchPendingChange,
			edits: UpdateCrewActionPayload[],
		}>(),
		"Pending Change Removed": props<{
			changeId: string,
		}>(),
	"Save Changes Clicked": emptyProps(),
	"Set Autosave Enabled": props<{ enabled: boolean }>(),
	"Set Overtime Tiers": props<{ tiers: { thresholdHours: number; multiplier: number }[] }>(),
	"Save Changes Success": props<{
			changeIds: string[],
		}>(),
	"Save Changes Partial Success": props<{
		succeededChangeIds: string[],
		failedChangeIds: string[],
	}>(),
	"Save Changes Error": props<{
		error: Error,
	}>(),
	"Discard Changes Clicked": emptyProps(),
		"Changes Discarded": emptyProps(),
		"Block Deleted": props<{
			eventId: string,
		}>(),
	},
});
